import { useState, useEffect, useRef } from 'react';
import { useUserData } from '../../context/UserDataContext';
import { testAllEndpoints } from '../../utils/endpointTester';

/**
 * EndpointTest component for testing API endpoints
 * @returns {JSX.Element} EndpointTest component
 */
function EndpointTest() {
  const { userData } = useUserData();
  const [siteName, setSiteName] = useState('');
  const [unitId, setUnitId] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const isRunningRef = useRef(false);
  
  // Initialize with data from userData if available
  useEffect(() => {
    // console.log("EndpointTest component mounted or userData changed");
    if (userData) {
      setSiteName(userData.siteName || '');
      setUnitId(userData.unitId || '');
    }
  }, [userData]);
  
  const handleRunTests = async () => {
    // Debounce to prevent multiple executions
    if (isRunningRef.current || loading) return;
    
    if (!siteName || !unitId) {
      alert('Please enter a site name and unit ID');
      return;
    }
    
    isRunningRef.current = true;
    // console.log(`Button clicked - Starting tests with site: ${siteName}, unit: ${unitId}`);
    setLoading(true);
    
    try {
      // console.log("About to call testAllEndpoints");
      const testResults = await testAllEndpoints(siteName, Number(unitId));
      // console.log("Received test results:", testResults);
      setResults(testResults);
    } catch (error) {
      // console.error('Error running tests:', error);
    } finally {
      setLoading(false);
      isRunningRef.current = false;
      // console.log("Test execution completed");
    }
  };
  
  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">API Endpoint Tester</h1>
      <p className="mb-4">
        This tool tests the connection between your frontend and the backend API endpoints.
      </p>
      
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Site Name:</label>
          <input
            type="text"
            value={siteName}
            onChange={e => setSiteName(e.target.value)}
            placeholder="e.g. Lab"
            className="w-full px-4 py-2 border rounded"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Unit ID:</label>
          <input
            type="number"
            value={unitId}
            onChange={e => setUnitId(e.target.value)}
            placeholder="e.g. 1"
            className="w-full px-4 py-2 border rounded"
          />
        </div>
        
        <button
          onClick={handleRunTests}
          disabled={loading}
          className={`px-4 py-2 rounded bg-blue-500 text-white font-bold ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'}`}
        >
          {loading ? 'Running Tests...' : 'Run Endpoint Tests'}
        </button>
      </div>
      
      {results && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-4">Test Results</h2>
          <p className="mb-2">
            <span className="font-semibold">Site:</span> {results.site}
          </p>
          <p className="mb-4">
            <span className="font-semibold">Unit:</span> {results.unit}
          </p>
          
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Summary</h3>
            <p>
              {results.endpoints.filter(endpoint => endpoint.success).length}/{results.endpoints.length} endpoints working
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Working Endpoints</h3>
            {results.endpoints.filter(endpoint => endpoint.success).length > 0 ? (
              <ul className="list-disc pl-5">
                {results.endpoints
                  .filter(endpoint => endpoint.success)
                  .map((endpoint, index) => (
                    <li key={`success-${index}`} className="mb-2">
                      <span className="text-green-600 font-semibold">✓</span>{' '}
                      {endpoint.name}
                      <div className="text-xs text-gray-600">{endpoint.url}</div>
                      <div className="text-xs text-gray-600">Response time: {endpoint.responseTime}ms</div>
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-red-500">No working endpoints found</p>
            )}
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-2">Failed Endpoints</h3>
            {results.endpoints.filter(endpoint => !endpoint.success).length > 0 ? (
              <ul className="list-disc pl-5">
                {results.endpoints
                  .filter(endpoint => !endpoint.success)
                  .map((endpoint, index) => (
                    <li key={`fail-${index}`} className="mb-4 pb-2 border-b">
                      <div>
                        <span className="text-red-600 font-semibold">✗</span>{' '}
                        {endpoint.name}
                      </div>
                      <div className="text-xs text-gray-600">{endpoint.url}</div>
                      <div className="text-sm text-red-500 mt-1">Error: {endpoint.error}</div>
                      {endpoint.status && (
                        <div className="text-xs text-gray-600">Status: {endpoint.status} {endpoint.statusText}</div>
                      )}
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-green-500">No failed endpoints - everything is working!</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EndpointTest; 